import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import {
    Landmark,
    Scale,
    LineChart,
    Users,
    FlaskConical,
    Leaf,
    Calendar,
    MapPin,
    Building2,
    Layers
} from 'lucide-react'
import PageHeader from '@shared/components/PageHeader'
import SEO from '@shared/components/SEO'


const FACTS = [
    { icon: Calendar, label: 'Established', value: '2019' },
    { icon: MapPin, label: 'Based in', value: 'Delhi' },
    { icon: Building2, label: 'Type', value: 'Think Tank' },
    { icon: Layers, label: 'Approach', value: 'Multidisciplinary' }
]

const FOCUS = [
    { icon: Users, title: 'Social', text: 'Society, communities and the quality of public life.' },
    { icon: Landmark, title: 'Political', text: 'Institutions, governance and civil & political society.' },
    { icon: LineChart, title: 'Economic', text: 'Policy questions shaping growth and welfare.' },
    { icon: Scale, title: 'Legal', text: 'Public law and the rules that order institutions.' },
    { icon: FlaskConical, title: 'Scientific', text: 'Evidence-led approaches to policy problems.' },
    { icon: Leaf, title: 'Environmental', text: 'Sustainability and environmental governance.' }
]

export default function AboutUs() {
    return (
        <>
            <SEO
                title="About Us"
                description="The Centre for Study of Politics and Governance — a Delhi-based think tank established in 2019, bringing together intellectuals across disciplines to offer enduring public-policy solutions."
                path="/about-us"
            />
            <PageHeader
                eyebrow="The Centre"
                title="About the Centre"
                lede="A Delhi-based think tank bringing innovative minds together to shape effective, enduring public policy."
            />

            <section className="section">
                <div className="container-prose">
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.55 }}
                        className="prose-body dropcap dropcap--accent"
                    >
                        <p>
                            The Centre for the Study of Politics and Governance was established in
                            2019. It is a Delhi-based think tank set up through the collaboration of
                            a wide range of intellectuals and other professionals from various
                            disciplines.
                        </p>
                        <p>
                            It aims to bring innovative minds from many fields together to ideate on
                            questions of public policy — across social, political, economic, legal,
                            scientific and environmental issues — and to offer innovative, effective
                            and enduring policy solutions for the operative functioning of public
                            institutions.
                        </p>
                        <p>
                            Indeed, the Centre investigates the nature, role and significance of
                            civil and political society as these affect the quality of life of the
                            people.
                        </p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-px"
                        style={{ background: 'var(--color-rule)', border: '1px solid var(--color-rule)' }}
                    >
                        {FACTS.map((f) => {
                            const Icon = f.icon
                            return (
                                <div
                                    key={f.label}
                                    className="px-5 py-6 text-center"
                                    style={{ background: 'var(--color-paper-50)' }}
                                >
                                    <Icon size={22} className="mx-auto" style={{ color: 'var(--color-gold-700)' }} />
                                    <div
                                        className="mt-3 text-[10px] uppercase font-semibold"
                                        style={{ letterSpacing: '0.16em', color: 'var(--color-ink-400)' }}
                                    >
                                        {f.label}
                                    </div>
                                    <div
                                        className="font-display mt-1"
                                        style={{ fontSize: '1.25rem', color: 'var(--color-ink-700)' }}
                                    >
                                        {f.value}
                                    </div>
                                </div>
                            )
                        })}
                    </motion.div>

                    <div className="mt-16">
                        <div className="kicker">Areas of focus</div>
                        <div
                            className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-px"
                            style={{ background: 'var(--color-rule)', border: '1px solid var(--color-rule)' }}
                        >
                            {FOCUS.map((f, i) => {
                                const Icon = f.icon
                                return (
                                    <motion.div
                                        key={f.title}
                                        initial={{ opacity: 0, y: 12 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.45, delay: (i % 3) * 0.06 }}
                                        className="p-7"
                                        style={{ background: 'var(--color-paper-50)' }}
                                    >
                                        <Icon size={22} style={{ color: 'var(--color-gold-700)' }} />
                                        <h3
                                            className="font-display mt-3.5"
                                            style={{ fontSize: '1.15rem', color: 'var(--color-ink-700)', letterSpacing: '-0.01em' }}
                                        >
                                            {f.title}
                                        </h3>
                                        <p
                                            className="mt-2 text-[14px] leading-relaxed"
                                            style={{ color: 'var(--color-ink-500)' }}
                                        >
                                            {f.text}
                                        </p>
                                    </motion.div>
                                )
                            })}
                        </div>
                    </div>

                    <motion.figure
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="my-16 md:my-20 text-center"
                    >
                        <hr className="rule-double mb-9" />
                        <blockquote
                            className="font-display mx-auto"
                            style={{
                                maxWidth: '44rem',
                                fontSize: 'clamp(1.35rem, 2.8vw, 1.9rem)',
                                lineHeight: 1.34,
                                letterSpacing: '-0.02em',
                                color: 'var(--color-ink-700)'
                            }}
                        >
                            “The Centre encourages efforts to revive and reinvigorate the role of
                            youth in civil and political society.”
                        </blockquote>
                        <hr className="rule-double mt-9" />
                    </motion.figure>

                    <div
                        className="flex flex-wrap items-center justify-between gap-5 pt-9"
                        style={{ borderTop: '1px solid var(--color-rule-strong)' }}
                    >
                        <p className="lede" style={{ maxWidth: '32rem', fontSize: '1.05rem' }}>
                            Explore what guides the Centre and the people behind it.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Link to="/vision-mission" className="btn btn-ghost">
                                Vision &amp; Mission
                            </Link>
                            <Link to="/board-of-trustees" className="btn btn-ghost">
                                Board of Trustees
                            </Link>
                            <Link to="/events-collaboration" className="btn btn-gold">
                                Events &amp; Collaboration →
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </>
    )
}
